import { useMemo } from "react";
import {
  useMutation,
  useQuery,
  useQueries,
  type QueryKey,
  type UseQueryOptions
} from "react-query";

import {
  getDataObjectDetails,
  getDataObjectsForWorkflow,
  getDataObjectTypes,
  getDataObjectWorkflowRelationActions,
  getDataObjectActivityLogs,
  getRelatedObjects,
  getObjectsStatuses,
  getDataObjectTimeSeries,
  getCopySourceObjects,
  getObjectScreeningActivityLogs,
  getCopyStep,
  resetCopyStep,
  getDataObjectAccessibleSections,
  getObjectTypeTranslations,
  getDataObjectFileUrls,
  getDataObjectList,
  exportObjectReport,
  getObjectAccessibleSchema,
  getSurveyTableFieldData,
  exportSurveyTableFieldData,
  getSelectionTableFieldData,
  getSelectionResponse,
  selectObject,
  deselectObject,
  type GetDataObjectListParams
} from "../services/dataObjects.services";

import type {
  CreateDataObjectDetailsModelReturn,
  CreateDataObjectTypesModelWithTranslationsReturn,
  CreateDataObjectWorkflowRelationActionsModelReturn,
  DataObjectWorkflowRelation,
  GetDataObjectDetailsRequestParams,
  RelatedObjectsFilters,
  GetSelectionTableFieldDataParams,
  GetSelectionTableFieldDataReturn,
  GetSelectionResponseParams,
  GetSelectionResponseReturn,
  SelectObjectParams,
  DeselectObjectParams,
  BreadcrumbElement,
  BreadcrumbHierarchy,
  UseBreadcrumbHierarchyReturn
} from "../types/dataObjectsAPIs.types";
import type {
  GetSurveyTableFieldDataParams,
  GetSurveyTableFieldDataReturn
} from "../types/dataObjectsSurveyTable.types";
import type {
  UseMutationExtraConfigType,
  UseQueryReturnType
} from "@certa/common/types";

/**
 * @deprecated - use useGetDataObjectsDynamicAPI instead
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const useGetDataObjectsForWorkflow = (
  workflowId: number,
  config: UseQueryOptions<
    UseQueryReturnType<typeof getDataObjectsForWorkflow>
  > = {
    enabled: true
  }
) => {
  return useQuery({
    enabled: !!workflowId && !!config?.enabled,
    retry: 0,
    queryKey: ["dataObjects", workflowId],
    queryFn: () => getDataObjectsForWorkflow(workflowId)
  });
};

const getObjectTypesQuery = () => {
  return {
    queryKey: ["dataObjectTypes"] as QueryKey,
    queryFn: getDataObjectTypes,
    staleTime: Infinity,
    refetchOnWindowFocus: false
  };
};

export const useGetDataObjectTypes = (
  config?: UseQueryOptions<UseQueryReturnType<typeof getDataObjectTypes>>
) => {
  return useQuery({
    ...config,
    ...getObjectTypesQuery()
  });
};

type UseGetDataObjectsDynamicAPIParams = {
  filters: {
    objectId?: string;
    objectTypeTag?: string[];
    outgoingRelationshipsToObjectIds?: string[];
    outgoingRelationshipsForwardRelationName?: string[];
    workflowMappingWorkflowId?: string[];
    relatedVia?:
      | {
          relationName: string;
          relatedObjectUid: string;
          objectTypeTag: string;
          relatedObjectId?: string;
        }
      | {
          relationName: string;
          relatedObjectUid?: string;
          objectTypeTag: string;
          relatedObjectId: string;
        };
    statusTagIn?: string[];
    statusTagNotIn?: string[];
    nameIContains?: string;
    orderBy?: string;
  };
};

export const useGetDataObjectsDynamicAPI = (
  params: UseGetDataObjectsDynamicAPIParams,
  offset: number = 0,
  config: UseQueryOptions<UseQueryReturnType<typeof getDataObjectDetails>> = {
    enabled: true
  }
) => {
  const { filters } = params;

  const objectTypeTag = filters.objectTypeTag?.join(",");
  const outgoingRelationshipsToObjectIds =
    filters.outgoingRelationshipsToObjectIds?.join(",");
  const outgoingRelationshipsForwardRelationName =
    filters.outgoingRelationshipsForwardRelationName?.join(",");
  const workflowMappingWorkflowId =
    filters.workflowMappingWorkflowId?.join(",");
  const relatedVia = filters.relatedVia;
  const objectId = filters.objectId;
  // We need to sort created_at by descending order by default since the
  // recently created objects should be at the top of the list
  const orderBy = filters?.orderBy || "-created_at";
  const nameIContains = filters?.nameIContains;
  const statusTagIn = filters?.statusTagIn?.join(",");
  const statusTagNotIn = filters?.statusTagNotIn?.join(",");

  const transformedParams: GetDataObjectDetailsRequestParams = {
    data: {
      id: null, // TODO: Remove this once create workflow supports uid for object
      uid: null,
      name: null,
      data: null,
      logo: null,
      applied_badges: null,
      indicators: null,
      created_at: null,
      updated_at: null,
      object_type: {
        id: null,
        tag: null,
        schema: null,
        created_at: null,
        updated_at: null,
        ui_schema: null
      },
      status: {
        id: null,
        tag: null,
        name: null,
        color_code: null
      },
      workflow_mappings: {
        id: null,
        workflow: null,
        object_reference: null
      }
    },
    filters: {
      uid__in: objectId,
      object_type__tag__in: objectTypeTag,
      outgoing_relationships__to_object_id__in:
        outgoingRelationshipsToObjectIds,
      outgoing_relationships__forward_relation_name__in:
        outgoingRelationshipsForwardRelationName,
      workflow_mapping__wf_id__in: workflowMappingWorkflowId,
      order_by: orderBy,
      related_via: relatedVia
        ? relatedVia.relatedObjectUid
          ? {
              relation_name: relatedVia.relationName,
              related_object_uid: relatedVia.relatedObjectUid,
              object_type_tag: relatedVia.objectTypeTag
            }
          : relatedVia.relatedObjectId
            ? {
                relation_name: relatedVia.relationName,
                related_object_id: relatedVia.relatedObjectId,
                object_type_tag: relatedVia.objectTypeTag
              }
            : undefined
        : undefined,
      name__icontains: nameIContains,
      status_tag__in: statusTagIn,
      status_tag__not_in: statusTagNotIn
    }
  };

  return useQuery<CreateDataObjectDetailsModelReturn>({
    enabled: config.enabled,
    retry: 0,
    queryKey: ["dataObjectsDetails", transformedParams, offset],
    queryFn: () => getDataObjectDetails(transformedParams, offset)
  });
};

export type UseGetDataObjectWorkflowRelationActionsParams = {
  relation?: DataObjectWorkflowRelation;
  objectUid?: string;
} & (
  | {
      objectTypeTag?: string;
      workflowKindTag: string;
    }
  | {
      workflowKindTag?: string;
      objectTypeTag: string;
    }
  | {
      objectTypeTag: string;
      workflowKindTag: string;
    }
);

export const useGetDataObjectWorkflowRelationActions = (
  params: UseGetDataObjectWorkflowRelationActionsParams,
  config: UseQueryOptions<
    UseQueryReturnType<typeof getDataObjectWorkflowRelationActions>
  > = {
    enabled: true
  }
) => {
  const { objectTypeTag, workflowKindTag, relation, objectUid } = params;

  return useQuery({
    enabled: (!!objectTypeTag || !!workflowKindTag) && !!config?.enabled,
    queryKey: [
      "dataObjectWorkflowRelationActions",
      objectTypeTag,
      workflowKindTag,
      relation,
      objectUid
    ],
    queryFn: () =>
      getDataObjectWorkflowRelationActions({
        objectTypeTag,
        workflowKindTag,
        relation,
        objectUid
      })
  });
};

const getNextPayload = (
  currentResponse: CreateDataObjectWorkflowRelationActionsModelReturn[number]
): UseGetDataObjectWorkflowRelationActionsParams | null => {
  if (!currentResponse?.objectTypeTag || !currentResponse?.workflowKindTag) {
    return null;
  }

  if (currentResponse.relation === "initializer_object") {
    return {
      relation: "object_creator",
      objectTypeTag: currentResponse.objectTypeTag
    };
  }

  if (currentResponse.relation === "object_creator") {
    return {
      relation: "initializer_object",
      workflowKindTag: currentResponse.workflowKindTag
    };
  }

  return null;
};

async function fetchAllWorkflowRelations(
  payload: UseGetDataObjectWorkflowRelationActionsParams
): Promise<any[]> {
  let allRelations: any[] = [];
  let currentPayload: UseGetDataObjectWorkflowRelationActionsParams | null =
    payload;

  while (currentPayload) {
    const currentBatch =
      await getDataObjectWorkflowRelationActions(currentPayload);
    if (currentBatch.length === 0) break;

    allRelations = [...allRelations, ...currentBatch];

    // Get the next payload from the first item in the current batch
    // There will be only one item in the current batch
    const lastItem = currentBatch[0];
    currentPayload = getNextPayload(lastItem);
  }

  return allRelations;
}

export const useGetDataObjectWorkflowRelationChain = (
  params: UseGetDataObjectWorkflowRelationActionsParams,
  config: UseQueryOptions<
    UseQueryReturnType<typeof getDataObjectWorkflowRelationActions>
  > = {
    enabled: true
  }
) => {
  return useQuery({
    ...config,
    refetchOnWindowFocus: false,
    retry: 0,
    enabled:
      (!!params.objectTypeTag || !!params.workflowKindTag) && !!config?.enabled,
    queryKey: ["dataObjectWorkflowRelationChain", params] as QueryKey,
    queryFn: () => fetchAllWorkflowRelations(params)
  });
};

type UseGetDataObjectActivityLogsParams = {
  objectId: string;
};

export const useGetDataObjectActivityLogs = (
  params: UseGetDataObjectActivityLogsParams,
  config: UseQueryOptions<
    UseQueryReturnType<typeof getDataObjectActivityLogs>
  > = {
    enabled: true
  }
) => {
  const { objectId } = params;

  return useQuery({
    enabled: !!objectId,
    ...config,
    queryKey: ["dataObjectActivityLogs", objectId] as QueryKey,
    queryFn: () => getDataObjectActivityLogs(objectId)
  });
};

type UseGetRelatedObjectsParams = {
  objectReference: string;
  workflowId: number;
  relationPath?: string;
  filters?: RelatedObjectsFilters;
};

export const useGetRelatedObjects = (params: UseGetRelatedObjectsParams) => {
  const { objectReference, workflowId, relationPath, filters } = params;

  return useQuery({
    enabled: !!objectReference && !!workflowId,
    queryKey: [
      "relatedObjects",
      objectReference,
      workflowId,
      relationPath,
      ...(filters ? Object.values(filters) : [])
    ] as QueryKey,
    queryFn: () =>
      getRelatedObjects({ objectReference, workflowId, relationPath, filters })
  });
};

const TRANSLATIONS_KEY = ["objectTypeTranslations"];

export const useGetObjectTypeTranslations = ({
  config
}: {
  config?: UseQueryOptions<CreateDataObjectTypesModelWithTranslationsReturn>;
}) => {
  return useQuery({
    queryKey: TRANSLATIONS_KEY,
    queryFn: () =>
      getObjectTypeTranslations({
        data: {
          translated_ui_schema: null,
          tag: null,
          id: null,
          schema: null,
          ui_schema: null,
          filter_config: null
        }
      }),
    enabled: !!config?.enabled,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    ...config
  });
};

export const useGetObjectsStatuses = (
  config?: UseQueryOptions<UseQueryReturnType<typeof getObjectsStatuses>>
) => {
  return useQuery({
    // TODO: Check how statuses/last_updated_at/ endpoint
    // can be used to invalidate the cache
    cacheTime: 1000 * 60 * 60 * 1, // 1 hour
    ...config,
    queryKey: ["objectsStatuses"] as QueryKey,
    queryFn: getObjectsStatuses
  });
};

type UseGetDataObjectTimeSeriesParams = {
  objectId: string;
  identifierField?: string;
  dataPoints?: string[];
};

export const useGetDataObjectTimeSeries = (
  params: UseGetDataObjectTimeSeriesParams
) => {
  const { objectId, identifierField, dataPoints } = params;

  const transformedParams = {
    objectId,
    identifier_field: identifierField!,
    data_points: dataPoints!
  };

  return useQuery({
    enabled: !!objectId && !!identifierField && !!dataPoints?.length,
    queryKey: ["dataObjectTimeSeries", objectId] as QueryKey,
    queryFn: () => getDataObjectTimeSeries(transformedParams)
  });
};

type UseGetCopySourceObjectsParams = {
  stepId: number;
  offset?: number;
};

export const useGetCopySourceObjects = (
  params: UseGetCopySourceObjectsParams
) => {
  const { stepId, offset } = params;

  return useQuery({
    enabled: !!stepId,
    queryKey: ["copySourceObjects", stepId] as QueryKey,
    queryFn: () => getCopySourceObjects({ stepId, offset })
  });
};

export const useCopyStep = (extraConfig?: UseMutationExtraConfigType) => {
  return useMutation(getCopyStep, extraConfig);
};

export const useResetCopyStep = (extraConfig?: UseMutationExtraConfigType) => {
  return useMutation(resetCopyStep, extraConfig);
};

type UseGetObjectScreeningActivityLogsParams = {
  objectId: string;
};

export const useGetObjectScreeningActivityLogs = (
  params: UseGetObjectScreeningActivityLogsParams,
  config: UseQueryOptions<
    UseQueryReturnType<typeof getObjectScreeningActivityLogs>
  > = {
    enabled: true
  }
) => {
  const { objectId } = params;

  return useQuery({
    enabled: !!objectId,
    ...config,
    queryKey: ["objectScreeningActivityLogs", objectId] as QueryKey,
    queryFn: () => getObjectScreeningActivityLogs(objectId)
  });
};

type UseGetDataObjectAccessibleSectionsParams = {
  objectId: string;
};

export const useGetDataObjectAccessibleSections = (
  params: UseGetDataObjectAccessibleSectionsParams,
  config?: UseQueryOptions<
    UseQueryReturnType<typeof getDataObjectAccessibleSections>
  >
) => {
  const { objectId } = params;

  return useQuery({
    enabled: !!objectId,
    ...config,
    queryKey: ["dataObjectAccessibleSections", objectId] as QueryKey,
    queryFn: () => getDataObjectAccessibleSections(objectId)
  });
};

export const useGetDataObjectFileUrls = (
  objectId: string,
  config?: UseQueryOptions<UseQueryReturnType<typeof getDataObjectFileUrls>>
) => {
  return useQuery({
    ...config,
    enabled: !!objectId && !!config?.enabled,
    queryKey: ["dataObjectFileUrls", objectId] as QueryKey,
    queryFn: () => getDataObjectFileUrls(objectId)
  });
};

type UseGetDataObjectListParams = GetDataObjectListParams;

export const useGetDataObjectList = (
  params: UseGetDataObjectListParams,
  config: UseQueryOptions<UseQueryReturnType<typeof getDataObjectList>> = {
    enabled: true
  }
) => {
  return useQuery({
    enabled: !!params.objectTag && !!config?.enabled,
    queryKey: ["objectDataList", ...Object.values(params)],
    queryFn: () => getDataObjectList(params),
    ...config
  });
};

export const useExportObjectReport = (
  extraConfig?: UseMutationExtraConfigType
) =>
  useMutation(({ reportId, filters }: { reportId: number; filters?: any }) => {
    return exportObjectReport(reportId, filters);
  }, extraConfig);

export const useGetObjectAccessibleSchema = (
  objectTag: string,
  config?: UseQueryOptions<UseQueryReturnType<typeof getObjectAccessibleSchema>>
) => {
  return useQuery({
    enabled: !!objectTag && !!config?.enabled,
    retry: 0,
    queryKey: ["objectAccessibleSchema", objectTag],
    queryFn: () => getObjectAccessibleSchema(objectTag),
    staleTime: 5 * 60 * 1000,
    ...config
  });
};

export const useGetSurveyTableFieldData = (
  params: GetSurveyTableFieldDataParams,
  config: UseQueryOptions<GetSurveyTableFieldDataReturn> = {
    enabled: true
  }
) => {
  return useQuery({
    enabled: !!params.fieldId && !!config?.enabled,
    queryKey: ["surveyTableFieldData", params],
    queryFn: () => getSurveyTableFieldData(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...config
  });
};

export const useExportSurveyTableFieldData = (
  extraConfig?: UseMutationExtraConfigType
) => {
  return useMutation(
    (params: GetSurveyTableFieldDataParams) =>
      exportSurveyTableFieldData(params),
    extraConfig
  );
};

export const useGetSelectionTableFieldData = (
  params: GetSelectionTableFieldDataParams,
  config: UseQueryOptions<GetSelectionTableFieldDataReturn> = {
    enabled: true
  }
) => {
  return useQuery({
    enabled: !!params.fieldId && !!config?.enabled,
    queryKey: ["selectionTableFieldData", params],
    queryFn: () => getSelectionTableFieldData(params),
    staleTime: 5 * 60 * 1000,
    ...config
  });
};

export const useGetSelectionResponse = (
  params: GetSelectionResponseParams,
  config: UseQueryOptions<GetSelectionResponseReturn> = {
    enabled: true
  }
) => {
  return useQuery({
    enabled: !!params.workflowId && !!params.fieldTag && !!config?.enabled,
    queryKey: ["selectionResponse", params.workflowId, params.fieldTag],
    queryFn: () => getSelectionResponse(params),
    ...config
  });
};

export const useSelectObject = (extraConfig?: UseMutationExtraConfigType) => {
  return useMutation(
    (params: SelectObjectParams) => selectObject(params),
    extraConfig
  );
};

export const useDeselectObject = (extraConfig?: UseMutationExtraConfigType) => {
  return useMutation(
    (params: DeselectObjectParams) => deselectObject(params),
    extraConfig
  );
};

type BreadcrumbConfigLevel = {
  objectType: string;
  relationName: string;
};

type BreadcrumbQueryResult = {
  data?: UseQueryReturnType<typeof getDataObjectList>;
  isLoading: boolean;
  isError: boolean;
  error?: Error | unknown;
};

const parseBreadcrumbConfig = (config?: string): BreadcrumbConfigLevel[] => {
  if (!config) return [];
  const levels = config.split(".");
  return levels.slice(0, -1).map((objectType, idx) => ({
    objectType,
    relationName: levels.slice(idx, -1).reverse().join(".")
  }));
};

const toBreadcrumbElement = (
  query: BreadcrumbQueryResult
): BreadcrumbElement | null => {
  const result = query.data?.results?.[0];
  const objectType = query.data?.objectType;
  if (!result || !objectType) return null;

  return {
    objectTypeTag: objectType.tag,
    objectInstanceUid: result.objectUid,
    objectInstanceName: result.name || ""
  };
};

const buildHierarchy = (
  queries: BreadcrumbQueryResult[],
  hasConfig: boolean
): BreadcrumbHierarchy => {
  const parentElements = hasConfig
    ? queries
        .map(toBreadcrumbElement)
        .filter((el): el is BreadcrumbElement => !!el)
    : [];

  return parentElements;
};

const extractError = (queries: BreadcrumbQueryResult[]): string | undefined => {
  const errorQuery = queries.find(q => q.isError && q.error);
  if (!errorQuery) return undefined;

  const err = errorQuery.error;
  return err instanceof Error ? err.message : "Failed to load breadcrumb";
};

export const useBreadcrumbParents = (
  currentObjectUid: string,
  currentObjectTypeTag: string,
  breadcrumbConfig?: string
): UseBreadcrumbHierarchyReturn => {
  const configLevels = parseBreadcrumbConfig(breadcrumbConfig);
  const hasConfig = configLevels.length > 0;

  const hierarchyQueries = useQueries(
    configLevels.map(({ relationName }) => ({
      queryKey: [
        "breadcrumbHierarchy",
        currentObjectTypeTag,
        relationName,
        currentObjectUid
      ] as QueryKey,
      queryFn: async () => {
        if (!currentObjectUid)
          throw new Error(`Missing UID for ${currentObjectTypeTag}`);
        return getDataObjectList({
          objectTag: currentObjectTypeTag,
          relation_name: relationName,
          related_object_uid: currentObjectUid,
          ordering: "-created_at",
          limit: 1
        });
      },
      enabled: hasConfig && !!currentObjectUid,
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
      retry: 1
    }))
  );

  const breadcrumbHierarchy = useMemo(
    () => buildHierarchy(hierarchyQueries, hasConfig),
    [hierarchyQueries, hasConfig]
  );

  const isLoading = hierarchyQueries.some(q => q.isLoading);
  const hasError = hierarchyQueries.some(q => q.isError);
  const errorMessage = useMemo(
    () => extractError(hierarchyQueries),
    [hierarchyQueries]
  );

  return { breadcrumbHierarchy, isLoading, hasError, errorMessage };
};
